/**
 * @file AuthContext.js
 * @brief Provides local account authentication context backed by localStorage.
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";

const AuthContext = createContext(null);

/**
 * Provides local auth state (token + user) persisted in localStorage.
 */
export function LocalAuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        const storedUser = localStorage.getItem("local_user");
        const storedToken = localStorage.getItem("local_token");
        if (storedUser && storedToken) {
            setUser(JSON.parse(storedUser));
            setToken(storedToken);
        }
        setReady(true);
    }, []);

    const login = useCallback((userData, accessToken) => {
        setUser(userData);
        setToken(accessToken);
        localStorage.setItem("local_user", JSON.stringify(userData));
        localStorage.setItem("local_token", accessToken);
    }, []);

    const logout = useCallback(() => {
        setUser(null);
        setToken(null);
        localStorage.removeItem("local_user");
        localStorage.removeItem("local_token");
    }, []);

    const isAuthenticated = Boolean(user && token);
    const accessToken = token;

    return (
        <AuthContext.Provider value={{ user, token: accessToken, isAuthenticated, login, logout, ready }}>
            {children}
        </AuthContext.Provider>
    );
}

/**
 * Read the local auth context.
 */
export function useLocalAuth() {
    return useContext(AuthContext);
}
